import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVideo } from '../../../contexts/VideoContext';
import axios from 'axios';
import './Steps.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

const Step7Generate = () => {
  const { videoData, prevStep, updateVideoData } = useVideo();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Validate required fields
  const missingFields = [];
  if (!videoData.daycareId) missingFields.push('daycare info');
  if (!videoData.petName) missingFields.push('pet name');
  if (!videoData.emotion) missingFields.push('emotion');
  if (!videoData.activities || videoData.activities.length === 0) missingFields.push('activities');

  const handleStartOver = () => {
    // Clear wizard and restart
    localStorage.removeItem('barkbacks_video_wizard');
    updateVideoData({
      petName: '',
      photoUrl: '',
      selectedBreed: '',
      emotion: '',
      activities: [],
      currentStep: 1,
    });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');

    try {
      // Create video generation job
      const response = await axios.post(
        `${API_URL}/api/customer/videos/create`,
        {
          daycareId: videoData.daycareId,
          petName: videoData.petName,
          photoUrl: videoData.photoUrl,
          breed: videoData.selectedBreed,
          emotion: videoData.emotion,
          activities: videoData.activities,
        }
      );

      const { jobId } = response.data;

      // Redirect to video status page
      navigate(`/app/video/${jobId}`);
    } catch (err) {
      console.error('Error generating video:', err);
      setError(
        err.response?.data?.error ||
        'Failed to start video generation. Please try again.'
      );
      setGenerating(false);
    }
  };

  // Show error if missing required fields
  if (missingFields.length > 0) {
    return (
      <div className="wizard-step">
        <div className="step-content">
          <div className="step-icon">⚠️</div>
          <h2 className="step-title">Missing Information</h2>
          <p className="step-description">
            Some required information is missing. This can happen if you refreshed the page or came back from payment.
          </p>

          <div className="error-message" style={{ marginTop: '20px' }}>
            Missing: {missingFields.join(', ')}
          </div>

          <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
            Please start a new video to enter all the required information.
          </p>

          <div className="step-navigation" style={{ marginTop: '30px' }}>
            <button
              type="button"
              className="btn-primary"
              onClick={handleStartOver}
            >
              Start New Video
              <span className="btn-icon">🎬</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-step">
      <div className="step-content">
        <div className="step-icon">🎬</div>
        <h2 className="step-title">Ready to Generate!</h2>
        <p className="step-description">
          Your video is ready to be created. Click below to start the magic!
        </p>

        <div className="review-card">
          <div className="review-item">
            <h3 className="review-label">What happens next?</h3>
            <ul style={{ textAlign: 'left', lineHeight: '1.8' }}>
              <li>🎨 We'll create a personalized video for {videoData.petName}</li>
              <li>🎭 Featuring a {videoData.emotion} mood</li>
              <li>🎬 With activities: {videoData.activities.join(', ')}</li>
              <li>⏱️ Generation takes about 2-3 minutes</li>
              <li>📧 You'll receive a notification when it's ready</li>
            </ul>
          </div>

          {videoData.videosRemaining && (
            <div className="review-item">
              <h3 className="review-label">Credits Remaining</h3>
              <p className="review-value">
                {videoData.videosRemaining} video{videoData.videosRemaining > 1 ? 's' : ''} left
              </p>
            </div>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Navigation */}
        <div className="step-navigation">
          <button
            type="button"
            className="btn-secondary"
            onClick={prevStep}
            disabled={generating}
          >
            <span className="btn-icon">←</span>
            Back
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <>
                <span className="spinner-small"></span>
                Generating...
              </>
            ) : (
              <>
                Generate Video
                <span className="btn-icon">🎬</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step7Generate;
